import { PAR_PRICE, loanOfficers, tierPlans } from '../data/commissionData'
import type {
  ColumnKey,
  EstimatorColumn,
  LoanOfficer,
  LoanScenarioRow,
  PipelineLoan,
  PipelineSummary,
  Tier,
  TierPlan,
  WhatIfInputs,
} from '../types/commission'

export function getOfficer(officerId: string): LoanOfficer {
  return loanOfficers.find((item) => item.id === officerId) ?? loanOfficers[0]
}

export function getPlan(planId: string): TierPlan {
  const plan = tierPlans.find((item) => item.id === planId)
  if (!plan) throw new Error(`Unknown tier plan: ${planId}`)
  return plan
}

export function findTierByUnits(plan: TierPlan, units: number): Tier {
  const whole = Math.max(0, Math.floor(units))
  const match = plan.tiers.find(
    (tier) => whole >= tier.minUnits && (tier.maxUnits === null || whole <= tier.maxUnits),
  )
  return match ?? plan.tiers[plan.tiers.length - 1]
}

export function getNextTier(plan: TierPlan, tier: Tier): Tier | null {
  const index = plan.tiers.findIndex((item) => item.id === tier.id)
  return plan.tiers[index + 1] ?? null
}

/** Overage dollars generated above par on a loan. */
export function calcOverageDollars(loanAmount: number, sellPrice: number, parPrice = PAR_PRICE) {
  return ((sellPrice - parPrice) / 100) * loanAmount
}

export function summarizePipeline(pipeline: PipelineLoan[]): PipelineSummary {
  return pipeline.reduce<PipelineSummary>(
    (acc, loan) => ({
      grossUnits: acc.grossUnits + 1,
      grossVolume: acc.grossVolume + loan.loanAmount,
      weightedUnits: acc.weightedUnits + loan.likelihood,
      weightedVolume: acc.weightedVolume + loan.loanAmount * loan.likelihood,
      weightedOverageDollars:
        acc.weightedOverageDollars +
        calcOverageDollars(loan.loanAmount, loan.sellPrice) * loan.likelihood,
    }),
    {
      grossUnits: 0,
      grossVolume: 0,
      weightedUnits: 0,
      weightedVolume: 0,
      weightedOverageDollars: 0,
    },
  )
}

/** Builds one column of the estimator (Actuals, Current Pacing, or What If). */
export function buildEstimatorColumn(options: {
  key: ColumnKey
  label: string
  plan: TierPlan
  goalUnits: number
  units: number
  volume: number
  overageDollars: number
}): EstimatorColumn {
  const { key, label, plan, goalUnits, units, volume, overageDollars } = options
  const tier = findTierByUnits(plan, units)
  const nextTier = getNextTier(plan, tier)

  const basePay = (volume * tier.basisPoints) / 10_000
  const tierBonus = units * tier.unitBonus
  const overageShare = overageDollars * tier.overageSplit

  return {
    key,
    label,
    units,
    volume,
    overageDollars,
    goalUnits,
    goalAttainment: goalUnits > 0 ? units / goalUnits : 0,
    tier,
    nextTier,
    unitsToNextTier: nextTier ? Math.max(0, nextTier.minUnits - units) : 0,
    unitsToGoal: Math.max(0, goalUnits - units),
    basePay,
    tierBonus,
    overageShare,
    totalPayout: basePay + tierBonus + overageShare,
  }
}

export function buildEstimatorColumns(
  officer: LoanOfficer,
  plan: TierPlan,
  whatIf: WhatIfInputs,
): EstimatorColumn[] {
  const pipeline = summarizePipeline(officer.pipeline)

  const actuals = buildEstimatorColumn({
    key: 'actuals',
    label: 'Actuals',
    plan,
    goalUnits: officer.goalUnits,
    units: officer.closedUnits,
    volume: officer.closedVolume,
    overageDollars: officer.closedOverageDollars,
  })

  const pacing = buildEstimatorColumn({
    key: 'pacing',
    label: 'Current Pacing',
    plan,
    goalUnits: officer.goalUnits,
    units: officer.closedUnits + pipeline.weightedUnits,
    volume: officer.closedVolume + pipeline.weightedVolume,
    overageDollars: officer.closedOverageDollars + pipeline.weightedOverageDollars,
  })

  const base = whatIf.includePipeline ? pacing : actuals
  const extraVolume = whatIf.extraUnits * whatIf.averageLoanAmount
  const extraOverage = calcOverageDollars(extraVolume, whatIf.sellPrice)

  const whatIfColumn = buildEstimatorColumn({
    key: 'whatIf',
    label: 'What If',
    plan,
    goalUnits: officer.goalUnits,
    units: base.units + whatIf.extraUnits,
    volume: base.volume + extraVolume,
    overageDollars: base.overageDollars + extraOverage,
  })

  return [actuals, pacing, whatIfColumn]
}

/**
 * What a single loan pays at each tier of the LO's plan. The "current" row is the
 * tier the loan would actually fund in, given units already closed.
 */
export function buildLoanScenarioRows(options: {
  plan: TierPlan
  unitsBefore: number
  loanAmount: number
  sellPrice: number
}): LoanScenarioRow[] {
  const { plan, unitsBefore, loanAmount, sellPrice } = options
  const unitsAfter = Math.floor(unitsBefore) + 1
  const landingTier = findTierByUnits(plan, unitsAfter)
  const overageDollars = calcOverageDollars(loanAmount, sellPrice)

  return plan.tiers.map((tier) => {
    const basePay = (loanAmount * tier.basisPoints) / 10_000
    const overageShare = overageDollars * tier.overageSplit
    const status: LoanScenarioRow['status'] =
      tier.id === landingTier.id ? 'current' : unitsAfter > tier.minUnits ? 'cleared' : 'locked'

    return {
      tier,
      basePay,
      unitBonus: tier.unitBonus,
      overageShare,
      total: basePay + tier.unitBonus + overageShare,
      status,
      unitsToReach: Math.max(0, tier.minUnits - unitsAfter),
    }
  })
}

export function statusLabel(status: LoanOfficer['status']): string {
  switch (status) {
    case 'legacy':
      return 'Legacy Banker'
    case 'elite':
      return 'Elite Banker'
    case 'custom':
      return 'Custom Plan'
    default:
      return 'Standard Banker'
  }
}
